import { useState, useMemo, useCallback } from 'react';
import {
  Button,
  Col,
  Row,
  Card,
  ButtonToolbar,
  ButtonGroup,
  Form,
} from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useNavigate, useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import setFieldTouched from 'final-form-set-field-touched';
import { v4 as uuidv4 } from 'uuid';

import cloneDeep from 'lodash.clonedeep';
import get from 'lodash.get';

import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import { settingsSet } from '../store/settings_slice';

import PicklistTemplateProductFormModal from './picklist_template_form/picklist_template_product_form_modal';
import PicklistTemplateProductList from './picklist_template_form/picklist_template_product_list';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderError, renderOffline } from '../components/render_helpers';
import SubmitButtonSpinner from '../components/submit_button_spinner';
import Field from '../components/form/rff_field';
import InputField from '../components/form/input_field';
import { coerceInput, pickValues, handleSubmitError } from '../lib/utils';
import {
  picklistTemplateFormPageQuery,
  picklistTemplateCreate as picklistTemplateCreateMutation,
  picklistTemplateUpdate as picklistTemplateUpdateMutation,
} from '../graphql/picklist_template_queries';
import * as updateFunctions from '../update_functions';
import { picklistTemplateDefaultValues } from '../defaults';
import { picklistTemplateFormValidator } from '../validators';
import { picklistTemplateWhiteList } from '../white_lists';

const PicklistTemplateForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const [showPicklistTemplateProductFormModal, setShowPicklistTemplateProductFormModal] =
    useState(false);
  const [selectedPicklistTemplateProduct, setSelectedPicklistTemplateProduct] = useState(
    {}
  );
  const currentUser = useSelector((state) => state.auth.user);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const [picklistTemplateCreate] = useMutation(picklistTemplateCreateMutation);
  const [picklistTemplateUpdate] = useMutation(picklistTemplateUpdateMutation);
  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
  } = useQuery(picklistTemplateFormPageQuery, {
    variables: {
      hasPicklistTemplateId: !!params.id,
      picklistTemplateId: params.id || 0,
    },
    notifyOnNetworkStatusChange: true,
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const initialValues = useMemo(() => {
    if (pageData?.picklistTemplate) {
      const values = pickValues(pageData.picklistTemplate, picklistTemplateWhiteList);
      return values;
    }
    return picklistTemplateDefaultValues;
  }, [pageData]);

  const selectableProducts = useMemo(() => {
    if (params.id) {
      // TODO does this need thinning?
      return get(pageData, 'productList', []).filter((p) => p.id);
    }
    return [];
  }, [pageData, params]);

  const handlePicklistTemplateProductEditClicked = useCallback(
    (picklistTemplateProductId) => {
      const picklistTemplateProducts = get(
        pageData,
        'picklistTemplate.picklistTemplateProducts',
        []
      );
      const picklistTemplateProduct = picklistTemplateProducts.find(
        (sci) => sci.id === parseInt(picklistTemplateProductId, 10)
      );
      if (picklistTemplateProduct) {
        setSelectedPicklistTemplateProduct(picklistTemplateProduct);
        setShowPicklistTemplateProductFormModal(true);
      }
    },
    [pageData]
  );

  const onCancel = () => {
    navigate('/picklist_templates');
  };

  const onFormSubmit = async (data) => {
    const uuid = uuidv4();
    const submitData = cloneDeep(data);
    let mutation;
    let mutationMessageAction;
    const input = coerceInput(submitData);
    const mutationData = {
      variables: { input },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'PicklistTemplateType',
        recordId: params.id || uuid,
      },
    };
    if (params.id) {
      mutation = picklistTemplateUpdate;
      mutationMessageAction = 'update';
      mutationData.variables.id = params.id;
      mutationData.context.mutationType = 'UPDATE';
      mutationData.update = updateFunctions.picklistTemplateUpdate;
      mutationData.optimisticResponse = updateFunctions.optimisticNew({
        mutationName: 'picklistTemplateUpdate',
        mutationData,
        currentData: pageData.picklistTemplate,
      });
    } else {
      mutation = picklistTemplateCreate;
      mutationData.context.mutationType = 'CREATE';
      mutationMessageAction = 'create';
      mutationData.update = updateFunctions.picklistTemplateCreate;
      mutationData.optimisticResponse = updateFunctions.optimisticNew({
        mutationName: 'picklistTemplateCreate',
        mutationData,
      });
    }
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await mutation(mutationData);
        toastSuccess(`Picklist Template ${mutationMessageAction} succeeded`);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        navigate('/picklist_templates');
      } catch (err) {
        const { errorMessage, submitErrors } = handleSubmitError(err);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        toastError(errorMessage);
        return submitErrors;
      }
    } else {
      mutation(mutationData);
      toastWarning(
        `Picklist Template ${mutationMessageAction} ok locally. Go online to make permanent`
      );
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
      navigate('/picklist_templates');
    }
    return undefined;
  };

  const handlePicklistTemplateProductFormModalClick = () => {
    setShowPicklistTemplateProductFormModal(true);
  };

  const handlePicklistTemplateProductFormModalCancel = () => {
    setShowPicklistTemplateProductFormModal(false);
    setSelectedPicklistTemplateProduct({});
  };

  const handlePicklistTemplateProductFormModalComplete = () => {
    pageRefetch();
    setShowPicklistTemplateProductFormModal(false);
    setSelectedPicklistTemplateProduct({});
  };

  const renderContent = () => (
    <>
      {params.id && (
        <PicklistTemplateProductFormModal
          show={showPicklistTemplateProductFormModal}
          products={selectableProducts}
          picklistTemplateId={get(pageData, ['picklistTemplate', 'id'])}
          picklistTemplateName={get(pageData, ['picklistTemplate', 'name'])}
          onCancel={handlePicklistTemplateProductFormModalCancel}
          onComplete={handlePicklistTemplateProductFormModalComplete}
          settingsTenant={settingsTenant}
          settingsOnline={settingsOnline}
          picklistTemplateProduct={selectedPicklistTemplateProduct}
        />
      )}
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">
                {params.id ? 'Edit Picklist Template' : 'New Picklist Template'}
              </h1>
            </div>
            <div className="float-end">
              <LinkContainer to="/picklist_templates">
                <Button className="me-2" variant="primary">
                  All Picklist Templates
                </Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <FinalForm
            initialValues={initialValues}
            onSubmit={(data) => onFormSubmit(data)}
            validate={picklistTemplateFormValidator}
            mutators={{ setFieldTouched }}
          >
            {({ handleSubmit, pristine, submitting }) => (
              <form noValidate>
                <Card>
                  <Card.Body>
                    <Field
                      name="name"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={8}
                      size="lg"
                    >
                      Picklist Template Name
                    </Field>
                    <Form.Group as={Row}>
                      <Col sm={12}>
                        <ButtonToolbar style={{ justifyContent: 'flex-end' }}>
                          <ButtonGroup className="me-2">
                            <Button
                              variant="danger"
                              onClick={onCancel}
                              disabled={submitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="primary"
                              disabled={pristine || submitting}
                              onClick={handleSubmit}
                            >
                              {submitting && <SubmitButtonSpinner />}
                              {params.id ? 'Update' : 'Create'}
                            </Button>
                          </ButtonGroup>
                        </ButtonToolbar>
                      </Col>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </form>
            )}
          </FinalForm>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col sm={12}>
          <Card>
            <Card.Header>Picklist Template Product Assemblies</Card.Header>
            <Card.Body>
              {params.id ? (
                <>
                  <PicklistTemplateProductList
                    picklistTemplateProducts={get(
                      pageData,
                      ['picklistTemplate', 'picklistTemplateProducts'],
                      []
                    )}
                    currentUser={currentUser}
                    settingsTenant={settingsTenant}
                    settingsOnline={settingsOnline}
                    onEdit={handlePicklistTemplateProductEditClicked}
                    onDelete={() => pageRefetch()}
                  />
                  <Button
                    className="me-2"
                    type="button"
                    variant="primary"
                    onClick={handlePicklistTemplateProductFormModalClick}
                  >
                    Add Picklist Template Assembly
                  </Button>
                </>
              ) : (
                <p>Create a new Picklist Template to begin adding Product Assemblies</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default PicklistTemplateForm;
